<template>
  <section>
    <ValidationObserver ref="observer">
      <b-row>
        <b-col v-if="!hideSubject" cols="12">
          <b-form-group>
            <template #label>
              <label class="form-group__label">
                {{ $t("dashboard.email_message_subject", "Subject") }}
              </label>
            </template>
            <b-form-input type="text" v-model="subject" />
          </b-form-group>
        </b-col>

        <b-col v-if="showReplyTo" cols="12">
          <b-form-group
            v-if="value.test_mode"
            :description="
              $t(
                'dashboard.email_message_test_mode_recipient_desc',
                'Use this field to send message to yourself'
              )
            "
          >
            <template v-slot:label>
              <label
                class="form-group__label form-group__label--required text-danger font-weight-bold"
              >
                {{ $t("dashboard.email_message_test_mode_recipient", "Test mode recipient") }}
              </label>
            </template>
            <b-form-input type="email" v-model="testModeRecepientEmail" required></b-form-input>
          </b-form-group>
          <b-form-group
            v-else
            :description="
              $t(
                'dashboard.email_message_replay_to_desc',
                'Use this field to resend message to person'
              )
            "
          >
            <template v-slot:label>
              <label class="form-group__label form-group__label--required">
                {{ $t("dashboard.email_message_replay_to", "Replay To") }}
              </label>
            </template>
            <b-form-input type="email" v-model="replayTo" required></b-form-input>
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <ValidationProvider name="message" :rules="rules" v-slot="{ errors, valid }">
            <b-form-group>
              <template #label>
                <label class="form-group__label form-group__label--required">
                  {{
                    showReplyTo
                      ? $t("dashboard.email_message_data", "Message")
                      : $t("dashboard.sms_message_data2", "SMS")
                  }}
                </label>
              </template>
              <quill-editor-variables v-model="message" :tags="tagsloc" :simple="simple">
                <template #btn-content>
                  {{ $t("donor_dashboard.email_template_edit_title", "Email variables") }}
                </template>
                <template #description>
                  {{ $t("donor_dashboard.email_template_var_description", "variables") }}
                </template>
              </quill-editor-variables>
              <template #description>
                <span
                  v-if="maxLength"
                  :class="{ 'text-danger': smsBudgetExceeded || charactersLeft < 0 }"
                >
                  {{ charactersLeftText }}
                </span>
                <span
                  v-if="maxLength && smsEncodingInfo && smsEncodingInfo.isUnicode"
                  class="tw-block tw-mt-1 tw-text-sm tw-text-amber-700"
                >
                  Unicode SMS uses shorter segments (70/67 UTF-16 units vs 160/153 for GSM-7). Emoji
                  and some symbols count as two UTF-16 units.
                </span>
                <span v-if="smsBudgetExceeded" class="tw-block tw-mt-1 tw-text-sm tw-text-red-600">
                  Message exceeds the allowed number of SMS segments for this campaign.
                </span>
              </template>
              <b-form-invalid-feedback :state="valid">
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </ValidationProvider>
        </b-col>
      </b-row>
    </ValidationObserver>
  </section>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";
import quillEditorVariables from "@/common-components/ui-elements/quill-editor-variables.vue";
import { analyzeSmsEncoding, maxSegmentsFromBudget, smsFitsBudget } from "@/utils/sms-encoding";

const buildGetSet = (key) => {
  return {
    get() {
      return this.value[key] || "";
    },
    set(v) {
      this.$set(this.value, key, v);
    },
  };
};

export default {
  components: { quillEditorVariables, ValidationObserver, ValidationProvider },

  data() {
    return {
      smsBudgetExceeded: false,
    };
  },

  props: {
    value: {
      type: Object,
      default: () => {},
    },
    tags: {
      type: Array,
      default: () => [],
    },
    maxLength: {
      type: Number,
      default: 0,
    },
    simple: Boolean,
    hideSubject: Boolean,
    showReplyTo: Boolean,
  },

  computed: {
    subject: buildGetSet("subject"),
    replayTo: buildGetSet("replay_to"),
    message: buildGetSet("message"),
    testModeRecepientEmail: buildGetSet("test_mode_recepient_email"),
    tagsloc() {
      return this.tags;
    },
    rules() {
      if (this.maxLength > 0) {
        return "required";
      }
      return "required";
    },
    smsEncodingInfo() {
      if (!this.maxLength) {
        return null;
      }
      return analyzeSmsEncoding(this.message || "");
    },
    /** Segment budget from campaign limit (legacy: multiples of ~160 GSM septets). */
    smsMaxSegments() {
      return maxSegmentsFromBudget(this.maxLength);
    },
    charactersLeft() {
      if (!this.maxLength || !this.smsEncodingInfo) {
        return 0;
      }
      return this.smsMaxSegments - this.smsEncodingInfo.segments;
    },
    charactersLeftText() {
      if (!this.maxLength || !this.smsEncodingInfo) {
        return "";
      }
      const enc = this.smsEncodingInfo.encoding === "gsm-7" ? "GSM-7" : "Unicode";
      return `(${this.smsEncodingInfo.units} ${enc} units, ${this.smsEncodingInfo.segments}/${this.smsMaxSegments} segments)`;
    },
  },

  watch: {
    message() {
      this.smsBudgetExceeded = false;
    },
  },

  methods: {
    validate() {
      return new Promise((resolve, reject) => {
        this.$refs.observer.validate().then((valid) => {
          if (!valid) {
            reject(new Error("validation failed"));
            return;
          }
          if (this.maxLength > 0 && !smsFitsBudget(this.message || "", this.maxLength)) {
            this.smsBudgetExceeded = true;
            reject(new Error("sms segment budget exceeded"));
            return;
          }
          this.smsBudgetExceeded = false;
          resolve();
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped></style>
